// ─────────────────────────────────────────────────────────────────
// Glass-i — Stage 6B.8A-3C: simplified UPVC / Aluminium Profile popup.
//
// Horizontal label layout matching the Glass screenshot reference.
// Maps onto inventory_items + item_profile_specs.
// ─────────────────────────────────────────────────────────────────

import { Checkbox, Col, Form, Input, InputNumber, Select } from 'antd';
import SimpleCreateModalShell from './SimpleCreateModalShell';
import TripleSalesRateInput from './TripleSalesRateInput';
import { SIMPLE_KINDS, PROFILE_MATERIAL_OPTIONS, TAXABILITY_OPTIONS } from '../../simpleCreateConstants';
import { ItemCategorySelect, UomSelect, BrandSelect, GstRateSelect, StockItemSelect } from './simpleCreateFields';

const GST_RULE = {
  validator: (_, v) =>
    v == null || v === '' || (Number(v) >= 0 && Number(v) <= 100)
      ? Promise.resolve()
      : Promise.reject(new Error('GST must be between 0 and 100'))
};
const PRICE_RULE = {
  validator: (_, v) =>
    v == null || v === '' || Number(v) >= 0
      ? Promise.resolve()
      : Promise.reject(new Error('Selling Price cannot be negative'))
};
const LENGTH_RULE = {
  validator: (_, v) =>
    v == null || v === '' || Number(v) >= 0
      ? Promise.resolve()
      : Promise.reject(new Error('Length cannot be negative'))
};

export default function CreateProfileModal({
  open, onClose, onSaved, editRecord = null, initialValues: initialValuesProp = null,
  viewOnly = false
}) {
  const initialValues = (editRecord || viewOnly) && initialValuesProp
    ? { ...initialValuesProp,
        _stockMode: initialValuesProp.sourceStockItemId ? 'derived' : 'self' }
    : { taxabilityType: 'taxable', salesRate: 0, _stockMode: 'self' };
  return (
    <SimpleCreateModalShell
      kind={SIMPLE_KINDS.PROFILE}
      open={open}
      onClose={onClose}
      onSaved={onSaved}
      editRecord={editRecord}
      viewOnly={viewOnly}
      initialValues={initialValues}
      render={({ form }) => (
        <>
          {/* Row 1 — Product Name + Brand */}
          <Col xs={24} md={12}>
            <Form.Item
              label="Product Name"
              name="itemName"
              required
              rules={[{ required: true, message: 'Product Name is required' }]}
            >
              <Input placeholder="e.g., UPVC Frame 60mm — White" autoFocus />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item label="Brand" name="brandId">
              <BrandSelect placeholder="Pick from Brand Master…" />
            </Form.Item>
          </Col>

          {/* Row 2 — Stock Name (75%) + Is Stock toggle (25%) */}
          <Form.Item name="sourceStockItemId" hidden><Input /></Form.Item>
          <Form.Item name="stockDesc"         hidden><Input /></Form.Item>
          <Form.Item name="_stockMode"        hidden><Input /></Form.Item>
          <Col xs={24} md={18}>
            <Form.Item label="Stock Name" tooltip="Optional — pick an existing base stock item or type a description.">
              <StockItemSelect
                mode={Form.useWatch('_stockMode', form) || 'self'}
                pickedId={Form.useWatch('sourceStockItemId', form)}
                textValue={Form.useWatch('stockDesc', form)}
                productName={Form.useWatch('itemName', form)}
                excludeId={editRecord && editRecord.id}
                onPick={(id) => form.setFieldValue('sourceStockItemId', id)}
                onChangeText={(text) => form.setFieldValue('stockDesc', text)}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={6} style={{ alignSelf: 'stretch', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Checkbox
              checked={(Form.useWatch('_stockMode', form) || 'self') === 'self'}
              onChange={(e) => {
                const checked = e.target.checked;
                form.setFieldValue('_stockMode', checked ? 'self' : 'derived');
                if (checked) {
                  form.setFieldValue('sourceStockItemId', null);
                  form.setFieldValue('stockDesc', form.getFieldValue('itemName') || '');
                } else {
                  form.setFieldValue('stockDesc', '');
                }
              }}
            >
              Is Stock?
            </Checkbox>
          </Col>

          {/* Row 3 — Label Name (full width) */}
          <Col xs={24}>
            <Form.Item label="Label Name" name="labelDesc">
              <Input placeholder="Display label name" />
            </Form.Item>
          </Col>

          {/* Row 4 — Profile Material + Profile Series */}
          <Col xs={24} md={12}>
            <Form.Item label="Profile Material" name="profileMaterialLabel">
              <Select
                placeholder="Select or type new…"
                allowClear
                showSearch
                options={PROFILE_MATERIAL_OPTIONS.map((o) => ({ value: o.label, label: o.label }))}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item label="Profile Series" name="profileSeries">
              <Input placeholder="e.g., 50 / 60 / 88" />
            </Form.Item>
          </Col>

          {/* Row 5 — Profile Color + Length */}
          <Col xs={24} md={12}>
            <Form.Item label="Profile Color" name="profileColor">
              <Input placeholder="e.g., White / Brown / Walnut" />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item label="Length (mm)" name="lengthMm" rules={[LENGTH_RULE]}>
              <InputNumber style={{ width: '100%' }} min={0} step={10} precision={2} placeholder="e.g., 5800" />
            </Form.Item>
          </Col>

          {/* Row 6 — Item Category + UOM */}
          <Col xs={24} md={12}>
            <Form.Item label="Item Category" name="itemCategoryId">
              <ItemCategorySelect placeholder="Select or type new…" />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item label="UOM" name="defaultUomId"
              rules={[{ required: true, message: 'UOM is required' }]}>
              <UomSelect placeholder="Select UOM (MTR, PCS, KGS…)" />
            </Form.Item>
          </Col>

          {/* Row 7 — HSN/SAC + GST */}
          <Col xs={24} md={12}>
            <Form.Item label="HSN / SAC" name="hsncode">
              <Input maxLength={12} placeholder="e.g., 7610" />
            </Form.Item>
          </Col>
          <Form.Item name="gstRatePercent" hidden><Input /></Form.Item>
          <Col xs={24} md={12}>
            <Form.Item label="GST (%)" name="gstRateId"
              rules={[{ required: true, message: 'GST rate is required' }]}>
              <GstRateSelect form={form} />
            </Form.Item>
          </Col>

          {/* Row 8 — Selling Price (triple: Sell / Min / Max) + Taxability */}
          <Form.Item name="minSalesRate" hidden><Input /></Form.Item>
          <Form.Item name="maxSalesRate" hidden><Input /></Form.Item>
          <Col xs={24} md={17}>
            <Form.Item
              label="Selling Price"
              name="salesRate"
              rules={[{ required: true, message: 'Selling Price is required' }, PRICE_RULE]}
            >
              <TripleSalesRateInput form={form} />
            </Form.Item>
          </Col>
          <Col xs={24} md={7}>
            <Form.Item
              label="Taxability"
              name="taxabilityType"
              labelCol={{ flex: '76px' }}
              wrapperCol={{ flex: 'auto' }}
            >
              <Select options={TAXABILITY_OPTIONS.map((o) => ({ value: o.value, label: o.label }))} />
            </Form.Item>
          </Col>

          {/* Row 9 — Remarks (full width) */}
          <Col xs={24}>
            <Form.Item label="Remarks" name="remarks">
              <Input.TextArea rows={2} maxLength={500} placeholder="Add any additional notes here..." />
            </Form.Item>
          </Col>
        </>
      )}
    />
  );
}
